import { useEffect, useRef } from 'react';
import { Button, Stack, Typography, useTheme } from '@mui/material';
import { resolveTokens } from '@/theme/theme';
import { ErrorIcon } from './icons';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { isApiError } from '@/api/errors';
import { isWindowRefusal } from '@/shared/servedWindow';
import { ReferenceLine } from './ReferenceLine';
import { RetryCountdown } from './RetryCountdown';
import { useRetryCountdown } from './useRetryCountdown';

export interface ErrorStateProps {
  /** The failure — read for its code (body copy), `requestId` and `Retry-After`; never rendered. */
  error: unknown;
  /** A catalog phrase naming what failed to load ("this account"), already translated. */
  what: string;
  /** Shows Retry; the caller reloads (the state never navigates). */
  onRetry?: (() => void) | undefined;
  /** `page`: a centred panel that replaces a route or tab. `section`: a compact inline block. */
  variant?: 'page' | 'section' | undefined;
}

/**
 * The one load-error state (TMM-B3, TMMAPP-0068): what failed in plain words, Retry, a
 * `Retry-After` countdown (visible number every second; announced to a screen reader twice, TMMAPP-0081),
 * and a copyable support reference. Title and body are catalog copy —
 * the body is `errorCopy`'s mapping of the error's code, never server text; a window refusal (a 422
 * naming `from` / `to`, on a page with no fields) has its own sentence (TMMAPP-0171 item 9). Action errors (a
 * failed save or submit) stay at the dialog or field; this is only for a load that replaced a
 * page, a tab, or a panel.
 */
export function ErrorState({ error, what, onRetry, variant = 'page' }: ErrorStateProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const page = variant === 'page';
  const titleRef = useRef<HTMLHeadingElement>(null);
  const requestId = isApiError(error) && error.requestId ? error.requestId : null;
  // The countdown restarts when a different error arrives (a failed retry), not on a re-render.
  const countdown = useRetryCountdown(error);
  const { delay, remaining } = countdown;

  // Focus goes to the title once, when a page-level error appears — never again on re-render.
  useEffect(() => {
    if (page) titleRef.current?.focus();
  }, [page]);

  const counting = onRetry !== undefined && delay !== null;

  return (
    <Stack
      role="alert"
      spacing={page ? 2.5 : 1.5}
      sx={
        page
          ? {
              alignItems: 'center',
              textAlign: 'center',
              mx: 'auto',
              maxWidth: 560,
              px: 2,
              py: { xs: 4, md: 8 },
            }
          : {
              alignItems: 'flex-start',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
            }
      }
    >
      {page && <ErrorIcon size={48} color={tokens.errorFg} />}
      <Typography
        ref={titleRef}
        tabIndex={-1}
        variant={page ? 'h2' : 'subtitle1'}
        component={page ? 'h2' : 'h3'}
      >
        {t('errors.loadTitle', { what })}
      </Typography>
      <Typography variant="body2">
        {isWindowRefusal(error)
          ? t('errors.windowRefused')
          : errorCopy(t, error, 'errors.loadFallback')}
      </Typography>
      {onRetry !== undefined && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            rowGap: 1,
            justifyContent: page ? 'center' : 'flex-start',
          }}
        >
          <Button
            variant="contained"
            size={page ? 'medium' : 'small'}
            disabled={remaining > 0}
            onClick={onRetry}
          >
            {t('common.retry')}
          </Button>
          {counting && <RetryCountdown {...countdown} />}
        </Stack>
      )}
      {requestId !== null && (
        <ReferenceLine requestId={requestId} align={page ? 'center' : 'start'} />
      )}
    </Stack>
  );
}
